import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    dcmjsCreateNewStructure,
    dcmjsGetString,
    dcmjsRemoveStructure,
    dcmjsSetString,
    dcmjsUpdateContourSequence,
} from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { DICOMApprovalStatus } from './dicomConstants';
import { DicomRoi } from './dicomRoi';
import { Modality } from './modality';
import { getDICOMDate, getDICOMTime } from './utils';

export enum ContourGeometricType {
    POINT = 'POINT',
    OPEN_PLANAR = 'OPEN_PLANAR',
    OPEN_NONPLANAR = 'OPEN_NONPLANAR',
    CLOSED_PLANAR = 'CLOSED_PLANAR',
}

export enum RoiGenerationAlgorithmType {
    MANUAL = 'MANUAL',
    AUTOMATIC = 'AUTOMATIC',
    SEMI_AUTOMATIC = 'SEMI_AUTOMATIC',
    UNKNOWN = '',
}

export enum RoiInterpretedType {
    CTV = 'CTV', // Clinical Target Volume
    GTV = 'GTV', // Gross Tumor Volume
    PTV = 'PTV', // Planning Target Volume
    EXTERNAL = 'EXTERNAL', // External patient contour, the BODY contour
    BOLUS = 'BOLUS', // patient bolus to be used for external beam therapy
    SUPPORT = 'SUPPORT', // external patient support device
    FIXATION = 'FIXATION', // external patient fixation or immobilization device
    CONTROL = 'CONTROL', // ROI to be used in control of dose optimization and calculation
    ORGAN = 'ORGAN', // patient organ
    OAR = 'OAR', // Organ at Risk
    MARKER = 'MARKER', // patient marker or marker on a localizer
    AVOIDANCE = 'AVOIDANCE', // region in which dose is to be minimized
    TREATED_VOLUME = 'TREATED_VOLUME', // Treated Volume
    IRRAD_VOLUME = 'IRRAD_VOLUME', // Irradiated Volume
    CONTRAST_AGENT = 'CONTRAST_AGENT', // volume into which a contrast agent has been injected
    REGISTRATION = 'REGISTRATION', // registration ROI
    ISOCENTER = 'ISOCENTER', // treatment isocenter to be used for external beam therapy
    CAVITY = 'CAVITY', // patient anatomical cavity
    BRACHY_CHANNEL = 'BRACHY_CHANNEL', // brachytherapy channel
    BRACHY_ACCESSORY = 'BRACHY_ACCESSORY', // brachytherapy accessory device
    BRACHY_SRC_APP = 'BRACHY_SRC_APP', // brachytherapy source applicator
    BRACHY_CHNL_SHLD = 'BRACHY_CHNL_SHLD', // brachytherapy channel shield
    DOSE_REGION = 'DOSE_REGION', // ROI to be used as a dose reference
    DOSE_MEASUREMENT = 'DOSE_MEASUREMENT', // ROI representing a dose measurement device, such as a chamber or TLD
    DEVICE = 'DEVICE', // device not addressed by another Defined Term
    UNKNOWN = '', // Unknown
}

export const ROI_TARGET_TYPES = [
    RoiInterpretedType.CTV,
    RoiInterpretedType.GTV,
    RoiInterpretedType.PTV,
];

export const ROI_OAR_TYPES = [
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.ORGAN,
    RoiInterpretedType.OAR,
    RoiInterpretedType.CAVITY,
];

export const AVAILABLE_ROI_TYPES = [
    ...ROI_OAR_TYPES,
    ...ROI_TARGET_TYPES,
    RoiInterpretedType.MARKER,
];

export const ROI_POINT_TYPES = [
    RoiInterpretedType.MARKER,
    RoiInterpretedType.ISOCENTER,
    RoiInterpretedType.REGISTRATION,
];

export type RoiIdentificationCodeSequence = {
    codeValue: string;
    codingSchemeDesignator: string;
    codingSchemeVersion: string;
    codeMeaning: string;
};

export class DicomContour {
    public constructor(
        public readonly type: ContourGeometricType,
        public readonly data: ReadonlyArray<ReadonlyVec3>,
        public readonly referencedSOPClassUID?: string,
        public readonly referencedSOPInstanceUID?: string
    ) {
        if (referencedSOPInstanceUID) {
            this.referencedSOPInstanceUID = referencedSOPInstanceUID.trim();
        }
        if (referencedSOPClassUID) {
            this.referencedSOPClassUID = referencedSOPClassUID.trim();
        }
    }

    public get length() {
        return this.data.length;
    }

    public get isValid(): boolean {
        if (this.length > 2) {
            //This is not ideal, but has to be done
            const a = this.data[0] as vec3;
            const delta = vec3.create();
            const dir = vec3.normalize(
                vec3.create(),
                vec3.subtract(delta, this.data[1] as vec3, a)
            );
            for (let i = 2; i < this.data.length; ++i) {
                vec3.subtract(delta, this.data[i] as vec3, a);
                vec3.normalize(delta, delta);
                const dot = vec3.dot(dir, delta);
                if (1.0 - Math.abs(dot) > 1e-6) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Returns a new DicomContour with updated Referenced SOP UIDs.
     * Contour vertex data is shared (not cloned) since it is ReadonlyArray.
     */
    public withReferencedUids(
        referencedSOPClassUID: string,
        referencedSOPInstanceUID: string
    ): DicomContour {
        return new DicomContour(
            this.type,
            this.data,
            referencedSOPClassUID,
            referencedSOPInstanceUID
        );
    }
}

export class DicomStructureSet extends DicomBase implements Iterable<DicomRoi> {
    public frameOfReferenceUid: string;

    /**
     * Map where key is the {@link DicomRoi.roiNumber} and value is the {@link DicomRoi}.
     */
    public rois: Map<number, DicomRoi>;

    /**
     * (0008,1155) Referenced SOP Instance UID Attribute
     * from (3006,0016) Contour Image Sequence Attribute
     * TODO: or alternatively could also get from (3006,0010) Referenced Frame of Reference Sequence Attribute
     */
    public readonly referencedSopUids: Set<string>;

    /**
     * (0008,1150) Referenced SOP Class UID Attribute
     */
    public readonly referencedSopClassUid: string;

    public readonly referencedSeriesUid: string;

    public readonly seriesInstanceUid: string;

    constructor(
        dataset: DcmJsDatasetWrapper,
        sopUid: string,
        frameOfReferenceUid: string,
        referencedSeriesUid: string,
        rois: Map<number, DicomRoi> | undefined = undefined,
        referencedSopUids: string[] = [],
        referencedSopClassUid: string,
        seriesInstanceUid: string
    ) {
        super(sopUid, Modality.RTSTRUCT, dataset);
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.referencedSeriesUid = referencedSeriesUid;
        this.rois = rois ?? new Map<number, DicomRoi>();
        this.referencedSopUids = new Set(referencedSopUids);
        this.referencedSopClassUid = referencedSopClassUid;
        this.seriesInstanceUid = seriesInstanceUid;
    }

    public get numberOfStructures(): number {
        return this.rois.size;
    }

    public addRoi(roi: DicomRoi): void {
        this._ensureCanModify();
        this.rois.set(roi.roiNumber, roi);
    }

    public getRoi(roiNumber: number): DicomRoi | undefined {
        return this.rois.get(roiNumber);
    }

    /**
     * Returns the smallest positive integer not currently used as an ROI number.
     */
    public getNextFreeRoiNumber(): number {
        let roiNumber = 1;

        while (this.rois.has(roiNumber)) {
            roiNumber++;
        }

        return roiNumber;
    }

    public clearRois() {
        this._ensureCanModify();
        this.rois.clear();
    }

    /**
     * Update the contour sequence with the ROIs contour data.
     */
    public updateContourSequence(roiNumber: number) {
        const roi = this.rois.get(roiNumber);
        if (roi) {
            dcmjsUpdateContourSequence(
                this.datasetWrapper,
                roi.roiNumber,
                roi.contours
            );
        }
    }

    /**
     * Structure Set Label Attribute Tag (3006,0002)
     */
    public get structureSetLabel(): string {
        return dcmjsGetString(this.datasetWrapper, 'StructureSetLabel');
    }

    public set structureSetLabel(label: string) {
        this._ensureCanModify();
        dcmjsSetString(this.datasetWrapper, 'StructureSetLabel', label);
    }

    /**
     * Structure Set Description Attribute Tag (3006,0006)
     */
    public get structureSetDescription(): string {
        return dcmjsGetString(this.datasetWrapper, 'StructureSetDescription');
    }

    public set structureSetDescription(description: string) {
        this._ensureCanModify();
        dcmjsSetString(
            this.datasetWrapper,
            'StructureSetDescription',
            description
        );
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    /**
     * Approval Status Attribute Tag (300E,0002)
     */
    public get approvalStatus(): DICOMApprovalStatus {
        return dcmjsGetString(
            this.datasetWrapper,
            'ApprovalStatus'
        ) as DICOMApprovalStatus;
    }

    public set approvalStatus(status: DICOMApprovalStatus) {
        dcmjsSetString(this.datasetWrapper, 'ApprovalStatus', status);
    }

    /**
     * Reviewer Name Attribute Tag (300E,0008)
     */
    public get reviewerName(): string {
        const value = dcmjsGetString(this.datasetWrapper, 'ReviewerName');
        // Handle DICOM Person Name (PN) format which comes as arrays with Alphabetic property
        if (Array.isArray(value) && value.length > 0 && value[0]?.Alphabetic) {
            return value[0].Alphabetic;
        }
        return value;
    }

    public set reviewerName(name: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewerName', name);
    }

    /**
     * Review Date Attribute Tag (300E,0004)
     */
    public get reviewDate(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewDate');
    }

    public set reviewDate(reviewDate: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewDate', reviewDate);
    }

    /**
     * Review Time Attribute Tag (300E,0005)
     */
    public get reviewTime(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewTime');
    }

    public set reviewTime(reviewTime: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewTime', reviewTime);
    }

    /**
     * Sets approval status with review dates (mutable version).
     * @param approvalStatus The new approval status
     * @param reviewerName The reviewer name
     */
    public setApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string
    ): void {
        let reviewDate = '';
        let reviewTime = '';

        if (approvalStatus === DICOMApprovalStatus.APPROVED) {
            reviewDate = this.reviewDate || getDICOMDate();
            reviewTime = this.reviewTime || getDICOMTime();
        }

        // Update the DICOM dataset directly
        dcmjsSetString(this.datasetWrapper, 'ApprovalStatus', approvalStatus);
        dcmjsSetString(
            this.datasetWrapper,
            'ReviewerName',
            approvalStatus === DICOMApprovalStatus.APPROVED ? reviewerName : ''
        );
        dcmjsSetString(this.datasetWrapper, 'ReviewDate', reviewDate);
        dcmjsSetString(this.datasetWrapper, 'ReviewTime', reviewTime);
    }

    /***
     * Creates a new ROI into this structure set.
     */
    public createRoi(
        interpretedType?: RoiInterpretedType,
        structureName: string = ''
    ): DicomRoi {
        this._ensureCanModify();
        // create a new default ROI into this structure set, including
        // inserting it into the DICOM dataset wrapper
        const roi = dcmjsCreateNewStructure(this);

        if (structureName !== '') {
            roi.name = structureName;
        } else {
            const isPoint =
                interpretedType !== undefined &&
                ROI_POINT_TYPES.includes(interpretedType);
            roi.name = this.generateNumberedName(isPoint ? 'POI' : 'ROI');
        }

        if (interpretedType) {
            roi.interpretedType = interpretedType;
        }

        return roi;
    }

    /**
     * Generates a unique numbered name like "ROI_1", "ROI_2" or
     * "POI_1", "POI_2" by finding the first unused suffix.
     */
    private generateNumberedName(baseName: string): string {
        const existingNames = new Set(
            Array.from(this.rois.values()).map((r) => r.name)
        );
        for (let i = 1; i < 1000; i++) {
            const candidate = `${baseName}_${i}`;
            if (!existingNames.has(candidate)) {
                return candidate;
            }
        }
        return `${baseName}_${Date.now()}`;
    }

    /**
     * Deletes an ROI directly
     */
    public deleteRoi(roiNumber: number): void {
        this._ensureCanModify();
        const roi = this.rois.get(roiNumber);
        if (!roi) {
            return; // ROI doesn't exist, nothing to delete
        }

        this.rois.delete(roiNumber);
        dcmjsRemoveStructure(this.datasetWrapper, roiNumber);
    }

    /**
     * Manufacturer Attribute Tag (0008,0070)
     */
    public get manufacturer(): string {
        return dcmjsGetString(this.datasetWrapper, 'Manufacturer');
    }

    /**
     * Manufacturer's Model Name Attribute Tag (0008,1090)
     */
    public get manufacturerModelName(): string {
        return dcmjsGetString(this.datasetWrapper, 'ManufacturerModelName');
    }

    /**
     * Software Versions Attribute Tag (0018,1020)
     */
    public get softwareVersions(): string {
        return dcmjsGetString(this.datasetWrapper, 'SoftwareVersions');
    }

    [Symbol.iterator](): Iterator<DicomRoi, DicomRoi, undefined> {
        return this.rois.values() as Iterator<DicomRoi, DicomRoi, undefined>;
    }

    /**
     * Compares two structure names using consistent domain rules.
     */
    static structureNamesEqual(a: string, b: string): boolean {
        return a.trim().toLowerCase() === b.trim().toLowerCase();
    }

    /**
     * Compares two structure names using case-sensitive trimmed comparison.
     */
    static structureNamesTrimmedEqual(a: string, b: string): boolean {
        return a.trim() === b.trim();
    }

    /**
     * Finds an ROI by name using domain-consistent name comparison rules.
     * @param name The structure name to search for
     * @returns The matching DicomRoi or undefined if not found
     */
    findRoiByName(name: string): DicomRoi | undefined {
        for (const roi of this.rois.values()) {
            if (DicomStructureSet.structureNamesEqual(roi.name, name)) {
                return roi;
            }
        }
        return undefined;
    }

    /**
     * Finds an ROI by case-sensitive name match with whitespace trimming.
     *
     * This method allows structures with different casing to coexist
     * (e.g., "Body" and "BODY") but treats whitespace variations as
     * conflicts (e.g., "Brain" and "  Brain  " are considered the same).
     */
    findRoiByTrimmedName(name: string): DicomRoi | undefined {
        for (const roi of this.rois.values()) {
            if (DicomStructureSet.structureNamesTrimmedEqual(roi.name, name)) {
                return roi;
            }
        }
        return undefined;
    }
}
